import { Alert, Divider, Grid, Snackbar } from "@mui/material";
import { IGetUserNotificationSettingsQuery } from "api/queries/usersAPI";
import { useStyles } from "../NotificationsModal.styles";
import MainAccordion from "components/MainAccordion/MainAccordion.view";
import { NotificationSwitch } from "./NotificationSwitch";
import { UsersEntity } from "entities/Users/UsersEntity";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { DefaultTranslationFieldsInTable, EmailNotificationTypesEnum } from "constants/constants";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { groupMachineCentersByProductionLine } from "./helpers";
import { translateDefaultEntity } from "utilities/localesUtilities";

export const AspenNotifications = ({
    notifications,
    refetch,
}: {
    notifications: IGetUserNotificationSettingsQuery["user_notification_settings"];
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const { enableNotification, apolloMutationError: errorUser } = UsersEntity.EnableUserNotificationSetting();
    const { enableMachineCenterNotification, apolloMutationError: errorMc } =
        UsersEntity.EnableUserMachineCenterNotificationSetting();

    const handleEnablingNotificationSetting = async (id: number, isEnabledChange: boolean) => {
        try {
            await enableNotification({
                variables: { settingId: id, change: isEnabledChange },
            });
            await refetch();
            setShowSnackbar(true);
            setSnackbarMessage(
                t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.update_success"),
            );
        } catch {
            setShowSnackbar(true);
            setSnackbarMessage(
                t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.update_error"),
            );
        }
    };
    const handleEnablingMachineCenterNotificationSetting = async (
        id: number,
        machineCenterId: number,
        isEnabledChange: boolean,
    ) => {
        try {
            await enableMachineCenterNotification({
                variables: {
                    where: { user_notification_settings_id: { _eq: id }, machine_center_id: { _eq: machineCenterId } },
                    change: isEnabledChange,
                },
            });
            await refetch();
            setShowSnackbar(true);
            setSnackbarMessage(
                t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.update_success"),
            );
        } catch {
            setShowSnackbar(true);
            setSnackbarMessage(
                t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notification_modal.update_error"),
            );
        }
    };

    return (
        <Grid container spacing={2}>
            {notifications.map((notification) => (
                <Grid key={notification.id} item xs={12}>
                    <NotificationSwitch
                        size={12}
                        margin
                        name={translateDefaultEntity(
                            i18n.language,
                            notification.email_notification_type as IGetUserNotificationSettingsQuery["user_notification_settings"][0]["email_notification_type"] & {
                                name: string;
                            },
                            "type",
                            DefaultTranslationFieldsInTable.EMAIL_NOTIFICATION_TYPE,
                        )}
                        isEnabled={notification.is_enabled}
                        handleChange={async () => {
                            await handleEnablingNotificationSetting(notification.id, !notification.is_enabled);
                        }}
                    />
                    {notification.email_notification_type.id ===
                        EmailNotificationTypesEnum.MACHINE_CENTER_DOWNTIME_TRIGGER &&
                        groupMachineCentersByProductionLine(notification.user_machine_center_notification_settings).map(
                            (pl) => (
                                <div key={pl.production_line_id} style={{ width: "100%" }}>
                                    <MainAccordion
                                        disabled={!notification.is_enabled}
                                        title={pl.name}
                                        fullWidth
                                        defaultExpanded={notification.is_enabled}
                                        defaultIsExpandedState={notification.is_enabled}
                                    >
                                        <Grid container spacing={2}>
                                            {pl.machine_centers.map((mc) => (
                                                <NotificationSwitch
                                                    size={4}
                                                    key={mc.machine_center_id}
                                                    name={mc.name}
                                                    isEnabled={mc.is_enabled}
                                                    handleChange={async () => {
                                                        await handleEnablingMachineCenterNotificationSetting(
                                                            notification.id,
                                                            mc.machine_center_id,
                                                            !mc.is_enabled,
                                                        );
                                                    }}
                                                />
                                            ))}
                                        </Grid>
                                    </MainAccordion>
                                </div>
                            ),
                        )}
                    <Divider className={classes.divider} />
                </Grid>
            ))}

            <Snackbar
                open={showSnackbar}
                onClose={() => {
                    setShowSnackbar(false);
                    setSnackbarMessage("");
                }}
                autoHideDuration={6000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={errorUser || errorMc ? "error" : "success"} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Grid>
    );
};
